import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { removeTemplate, addTemplate } from "../redux/slices/fieldNotifyTemplateSlice";

const AdminManageFieldNotify = () => {
    const templates = useSelector((state) => state.fieldNotifyTemplate.templates);
    const dispatch = useDispatch();

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState(null);

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        try {
            // Update Redux store directly
            dispatch(removeTemplate(id));
        } catch (err) {
            console.error("Error deleting notification template:", err);
            setError(`Failed to delete template: ${err.message || err}`);
        }
    };

    const handleSave = (updatedTemplate) => {
        try {
            // Generate a unique ID if this is a new template
            if (!updatedTemplate.id) {
                updatedTemplate.id = `${updatedTemplate.table}-${updatedTemplate.field}-${updatedTemplate.value}`;
            }
            
            // Update Redux store directly
            dispatch(addTemplate(updatedTemplate));
            
            setIsModalOpen(false);
            setSelectedTemplate(null);
        } catch (err) {
            console.error("Error saving notification template:", err);
            setError(`Failed to save template: ${err.message || err}`);
        }
    };

    const handleAdd = () => {
        setSelectedTemplate({
            id: "",
            table: "",
            field: "",
            value: "",
            recipients: [],
            subject: "",
            body: "",
            importance: "Medium",
        });
        setIsModalOpen(true);
    };

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Manage Notification Templates</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <button className="btn btn-primary mb-3" onClick={handleAdd}>
                Add Template
            </button>
            {templates && templates.length > 0 ? (
                <ul className="list-group">
                    {templates.map((template) => (
                        <li key={template.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{template.subject}</strong> - <span className="badge bg-primary">{template.importance}</span>
                            </div>
                            <div>
                                <button className="btn btn-sm btn-warning me-2" onClick={() => handleEdit(template)}>
                                    Edit
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(template.id)}>
                                    Delete
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="alert alert-info">No templates available. Click "Add Template" to create one.</div>
            )}

            {isModalOpen && (
                <div className="modal show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{selectedTemplate.id ? "Edit Template" : "Add Template"}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setIsModalOpen(false)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSave(selectedTemplate);
                                }}
                            >
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Table:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedTemplate.table}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, table: e.target.value })
                                            }
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Field:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedTemplate.field}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, field: e.target.value })
                                            }
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Value:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedTemplate.value}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, value: e.target.value })
                                            }
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Recipients (comma-separated):</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedTemplate.recipients.join(", ")}
                                            onChange={(e) =>
                                                setSelectedTemplate({
                                                    ...selectedTemplate,
                                                    recipients: e.target.value.split(",").map((email) => email.trim()),
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Subject:</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedTemplate.subject}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })
                                            }
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Importance:</label>
                                        <select
                                            className="form-select"
                                            value={selectedTemplate.importance}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, importance: e.target.value })
                                            }
                                        >
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Body:</label>
                                        <textarea
                                            className="form-control"
                                            rows="5"
                                            value={selectedTemplate.body}
                                            onChange={(e) =>
                                                setSelectedTemplate({ ...selectedTemplate, body: e.target.value })
                                            }
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManageFieldNotify;
